import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../scenario.css";
import { ISaveScenarioNode, IScenarioNode, IScenarioNodeTypes, ScenarioNodeType } from "../models/ILiveChatScenario";
import {
  createScenarioNode,
  deleteScenarioNode,
  getScenarioNodeTypes,
  getScenarioTokens,
  getScenarioTree,
  reorderScenarioNodes,
  setScenarioNodeActive,
  updateScenarioNode,
} from "../services/liveChatScenarioService";
import { getAllDepartments, getAllTicketSubjects } from "../../ticket-module/services/ticketService";
import { countNodes, descendantIds, findNode, flattenTree, nodeToPayload, scenarioErrorMessage, siblingsOf } from "../utils/scenarioUtils";
import ScenarioTree, { IDropTarget, fitsDepth } from "../components/scenario/ScenarioTree";
import ScenarioNodeEditor, { EditorMode } from "../components/scenario/ScenarioNodeEditor";
import ScenarioPreview from "../components/scenario/ScenarioPreview";
import DeleteScenarioNodeModal from "../components/scenario/DeleteScenarioNodeModal";

type Tab = "flow" | "preview";
interface IdName { id: string; name: string }
interface Toast { kind: "success" | "error"; text: string }

export default function LiveChatManagementPage() {
  const [tab, setTab] = useState<Tab>("flow");
  const [tree, setTree] = useState<IScenarioNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [meta, setMeta] = useState<IScenarioNodeTypes | null>(null);
  const [tokens, setTokens] = useState<string[]>([]);
  const [departments, setDepartments] = useState<IdName[]>([]);
  const [subjects, setSubjects] = useState<IdName[]>([]);

  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  /** Paletten bir konuma bırakılan yeni adımın yerleşeceği sıra */
  const placementRef = useRef<{ parentId: string | null; index: number } | null>(null);

  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<IScenarioNode | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [previewStart, setPreviewStart] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const maxDepth = meta?.maxDepth ?? 6;

  const showToast = (kind: Toast["kind"], text: string) => setToast({ kind, text });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  /* ------------------------------- Yükleme ------------------------------- */

  const reload = useCallback(async (): Promise<IScenarioNode[] | null> => {
    try {
      const res = await getScenarioTree(true);
      const data = res.data ?? [];
      setTree(data);
      setLoadError("");
      return data;
    } catch (err) {
      setLoadError(scenarioErrorMessage(err, "Senaryo yüklenemedi."));
      return null;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
    getScenarioNodeTypes().then((r) => setMeta(r.data)).catch(() => {});
    getScenarioTokens().then((r) => setTokens(r.data ?? [])).catch(() => {});
    getAllDepartments().then((r) => setDepartments((r.data ?? []).map((d: IdName) => ({ id: d.id, name: d.name })))).catch(() => {});
    getAllTicketSubjects().then((r) => setSubjects((r.data ?? []).map((s) => ({ id: s.id, name: s.name })))).catch(() => {});
  }, [reload]);

  /* ------------------------------ Düzenleyici ------------------------------ */

  const confirmDiscard = () =>
    !dirtyRef.current || window.confirm("Kaydedilmemiş değişiklikler kaybolacak. Devam edilsin mi?");

  const openEditor = (mode: EditorMode | null, force = false) => {
    if (!force && !confirmDiscard()) return false;
    dirtyRef.current = false;
    setEditor(mode);
    setEditorKey((k) => k + 1);
    return true;
  };

  /** Ağaç yenilenince açık düzenleyiciyi güncel adımla eşitle (kaydedilmemiş değişiklik yoksa). */
  const syncEditor = (fresh: IScenarioNode[], removedIds?: Set<string>) => {
    if (editor?.kind !== "edit") return;
    const node = findNode(fresh, editor.node.id);
    if (!node || removedIds?.has(editor.node.id)) { openEditor(null, true); return; }
    if (!dirtyRef.current) openEditor({ kind: "edit", node }, true);
  };

  const selectNode = (node: IScenarioNode) => {
    if (editor?.kind === "edit" && editor.node.id === node.id) return;
    openEditor({ kind: "edit", node });
  };

  const addNode = (parentId: string | null, nodeType?: ScenarioNodeType) => {
    if (parentId) {
      const depth = flattenTree(tree).find((f) => f.node.id === parentId)?.depth ?? 0;
      if (!fitsDepth(depth + 1, 0, maxDepth)) {
        showToast("error", `Senaryo en fazla ${maxDepth} seviye derinliğinde olabilir.`);
        return;
      }
    }
    if (openEditor({ kind: "create", parentId, nodeType })) placementRef.current = null;
  };

  const handleSave = async (payload: ISaveScenarioNode): Promise<string | null> => {
    if (!editor) return null;
    setSaving(true);
    try {
      let savedId: string;
      if (editor.kind === "create") {
        const res = await createScenarioNode(payload);
        savedId = res.data.id;
        // Paletten belli bir konuma bırakıldıysa oraya yerleştir
        const placement = placementRef.current;
        placementRef.current = null;
        if (placement && placement.parentId === payload.parentId) {
          const ids = siblingsOf(tree, payload.parentId).map((n) => n.id);
          if (placement.index < ids.length) {
            ids.splice(placement.index, 0, savedId);
            await reorderScenarioNodes({ parentId: payload.parentId, orderedNodeIds: ids }).catch(() => {});
          }
        }
      } else {
        await updateScenarioNode(editor.node.id, payload);
        savedId = editor.node.id;
      }
      const fresh = await reload();
      const node = fresh ? findNode(fresh, savedId) : null;
      dirtyRef.current = false;
      if (node) openEditor({ kind: "edit", node }, true);
      showToast("success", editor.kind === "create" ? "Adım oluşturuldu." : "Değişiklikler kaydedildi.");
      return null;
    } catch (err) {
      return scenarioErrorMessage(err, "Adım kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ Ağaç işlemleri ------------------------------ */

  const withBusy = async (id: string, work: () => Promise<void>) => {
    setBusyIds((s) => new Set(s).add(id));
    try { await work(); } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const toggleActive = (node: IScenarioNode) =>
    withBusy(node.id, async () => {
      try {
        await setScenarioNodeActive(node.id, !node.isActive);
        const fresh = await reload();
        if (fresh) syncEditor(fresh);
        showToast("success", node.isActive ? `"${node.text}" pasife alındı.` : `"${node.text}" aktif edildi.`);
      } catch (err) {
        showToast("error", scenarioErrorMessage(err, "Durum değiştirilemedi."));
      }
    });

  const reorder = async (parentId: string | null, ids: string[]) => {
    await reorderScenarioNodes({ parentId, orderedNodeIds: ids });
  };

  const moveStep = (node: IScenarioNode, dir: -1 | 1) =>
    withBusy(node.id, async () => {
      const ids = siblingsOf(tree, node.parentId).map((n) => n.id);
      const i = ids.indexOf(node.id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      try {
        await reorder(node.parentId ?? null, ids);
        const fresh = await reload();
        if (fresh) syncEditor(fresh);
      } catch (err) {
        showToast("error", scenarioErrorMessage(err, "Sıralama kaydedilemedi."));
      }
    });

  const dropNode = (node: IScenarioNode, target: IDropTarget) => {
    // Taşınan adım düzenleyicide açık ve kaydedilmemişse önce onay al
    if (editor?.kind === "edit" && editor.node.id === node.id && !confirmDiscard()) return;

    let parentId: string | null;
    let ids: string[];
    if (target.zone === "root") {
      parentId = null;
      ids = [...siblingsOf(tree, null).map((n) => n.id).filter((id) => id !== node.id), node.id];
    } else if (target.zone === "inside") {
      parentId = target.id;
      ids = [...siblingsOf(tree, parentId).map((n) => n.id).filter((id) => id !== node.id), node.id];
    } else {
      const t = target.id ? findNode(tree, target.id) : null;
      if (!t) return;
      parentId = t.parentId ?? null;
      ids = siblingsOf(tree, parentId).map((n) => n.id).filter((id) => id !== node.id);
      const at = ids.indexOf(t.id) + (target.zone === "after" ? 1 : 0);
      ids.splice(at, 0, node.id);
    }

    const parentChanged = (node.parentId ?? null) !== parentId;
    const before = siblingsOf(tree, parentId).map((n) => n.id);
    if (!parentChanged && before.join() === ids.join()) return;

    withBusy(node.id, async () => {
      try {
        if (parentChanged) {
          await updateScenarioNode(node.id, { ...nodeToPayload(node), parentId, sortOrder: ids.indexOf(node.id) });
        }
        await reorder(parentId, ids);
        const fresh = await reload();
        if (fresh) {
          dirtyRef.current = false;
          syncEditor(fresh);
        }
        const parentText = parentId ? findNode(tree, parentId)?.text : null;
        showToast("success", parentChanged
          ? `"${node.text}" ${parentText ? `"${parentText}" altına` : "açılış butonlarına"} taşındı.`
          : "Sıralama güncellendi.");
      } catch (err) {
        await reload();
        showToast("error", scenarioErrorMessage(err, "Adım taşınamadı."));
      }
    });
  };

  const dropNew = (nodeType: ScenarioNodeType, target: IDropTarget) => {
    let parentId: string | null;
    let index: number;
    if (target.zone === "root") {
      parentId = null;
      index = siblingsOf(tree, null).length;
    } else if (target.zone === "inside") {
      parentId = target.id;
      index = siblingsOf(tree, parentId).length;
    } else {
      const t = target.id ? findNode(tree, target.id) : null;
      if (!t) return;
      parentId = t.parentId ?? null;
      index = siblingsOf(tree, parentId).findIndex((n) => n.id === t.id) + (target.zone === "after" ? 1 : 0);
    }
    if (openEditor({ kind: "create", parentId, nodeType })) placementRef.current = { parentId, index };
  };

  const confirmDelete = async (includeChildren: boolean) => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await deleteScenarioNode(deleting.id, includeChildren);
      const removed = descendantIds(deleting);
      removed.add(deleting.id);
      const fresh = await reload();
      if (fresh) syncEditor(fresh, removed);
      showToast("success", `"${deleting.text}" silindi.`);
      setDeleting(null);
    } catch (err) {
      setDeleteError(scenarioErrorMessage(err, "Adım silinemedi."));
    } finally {
      setDeleteBusy(false);
    }
  };

  const parkFromDelete = async () => {
    if (!deleting) return;
    const node = deleting;
    setDeleting(null);
    await toggleActive(node);
  };

  /* -------------------------------- Görünüm -------------------------------- */

  const stats = useMemo(() => {
    const flat = flattenTree(tree);
    return {
      total: countNodes(tree),
      roots: tree.filter((n) => n.isActive).length,
      visible: flat.filter((f) => !f.hidden).length,
      hidden: flat.filter((f) => f.hidden).length,
    };
  }, [tree]);

  const typeLabels = useMemo(() => {
    const out: Partial<Record<ScenarioNodeType, string>> = {};
    meta?.nodeTypes?.forEach((t) => { out[t.name] = t.label; });
    return out;
  }, [meta]);

  const selectedId = editor?.kind === "edit" ? editor.node.id : null;

  return (
    <div className="lcs-page">
      {/* Başlık */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <h4 style={{ fontWeight: 800, color: "#111827", marginBottom: 4 }}>
            <i className="bi bi-diagram-3-fill me-2" style={{ color: "#4f46e5" }}></i>Canlı Destek Yönetimi
          </h4>
          <p style={{ color: "#6b7280", fontSize: ".88rem", margin: 0 }}>
            Müşteri canlı desteği açtığında karşılaşacağı butonları ve arkalarındaki adımları buradan tasarlayın.
          </p>
        </div>
        {!loading && !loadError && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              ["bi-diagram-3", `${stats.total} adım`, "#4f46e5", "#eef2ff"],
              ["bi-hand-index-thumb", `${stats.roots} açılış butonu`, "#0f766e", "#ccfbf1"],
              ["bi-eye-slash", `${stats.hidden} gizli`, "#6b7280", "#f3f4f6"],
            ].map(([icon, label, color, bg]) => (
              <span key={label} className="lcs-badge" style={{ fontSize: ".76rem", padding: "5px 11px", color, background: bg }}>
                <i className={`bi ${icon}`}></i>{label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Sekmeler */}
      <div className="lcs-tabs">
        <button type="button" className={`lcs-tab${tab === "flow" ? " lcs-tab--active" : ""}`} onClick={() => setTab("flow")}>
          <i className="bi bi-diagram-3-fill me-1"></i>Senaryo Akışı
        </button>
        <button type="button" className={`lcs-tab${tab === "preview" ? " lcs-tab--active" : ""}`}
          onClick={() => { setPreviewStart(null); setTab("preview"); }}>
          <i className="bi bi-phone-fill me-1"></i>Müşteri Önizlemesi
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div className="spinner-border" style={{ color: "#4f46e5" }} role="status" />
        </div>
      ) : loadError ? (
        <div className="lcs-note lcs-note--error" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span><i className="bi bi-exclamation-circle-fill me-1"></i>{loadError}</span>
          <button type="button" className="lcs-btn" onClick={() => { setLoading(true); reload().finally(() => setLoading(false)); }}>Tekrar Dene</button>
        </div>
      ) : tab === "flow" ? (
        <>
          {stats.roots === 0 && (
            <div className="lcs-note lcs-note--warn" style={{ marginBottom: 14 }}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>Aktif açılış butonu yok. Bu durumda müşterilere senaryo yerine eski hazır yanıt çipleri gösterilir.</span>
            </div>
          )}

          <div className="lcs-layout">
            <div>
              <ScenarioTree
                tree={tree}
                selectedId={selectedId}
                maxDepth={maxDepth}
                busyIds={busyIds}
                typeLabels={typeLabels}
                onSelect={selectNode}
                onAdd={addNode}
                onToggleActive={toggleActive}
                onMoveStep={moveStep}
                onDelete={(n) => { setDeleteError(""); setDeleting(n); }}
                onDropNode={dropNode}
                onDropNew={dropNew}
              />
              <div className="lcs-hint" style={{ margin: "8px 4px 0" }}>
                <i className="bi bi-arrows-move me-1"></i>
                Adımları <i className="bi bi-grip-vertical"></i> tutamağından sürükleyin: bir adımın ortasına bırakırsanız onun alt adımı olur, üst/alt kenarına bırakırsanız sırası değişir.
                Üstteki türlerden birini sürükleyip bırakarak yeni adım ekleyebilirsiniz.
              </div>
            </div>

            <div className={editor ? "lcs-editor-wrap--open" : "lcs-editor-wrap--empty"}>
              {editor ? (
                <ScenarioNodeEditor
                  key={editorKey}
                  mode={editor}
                  tree={tree}
                  meta={meta}
                  tokens={tokens}
                  departments={departments}
                  subjects={subjects}
                  saving={saving}
                  onSave={handleSave}
                  onClose={() => openEditor(null)}
                  onDelete={(n) => { setDeleteError(""); setDeleting(n); }}
                  onPreview={(id) => { setPreviewStart(id); setTab("preview"); }}
                  onDirtyChange={(d) => { dirtyRef.current = d; }}
                />
              ) : (
                <div className="lcs-card lcs-empty" style={{ position: "sticky", top: 16 }}>
                  <i className="bi bi-hand-index" style={{ fontSize: "2.4rem", display: "block", marginBottom: 10, color: "#c7d2fe" }}></i>
                  <div style={{ fontWeight: 800, color: "#374151", marginBottom: 6 }}>Düzenlemek için bir adım seçin</div>
                  <div style={{ fontSize: ".84rem", maxWidth: 380, margin: "0 auto 16px" }}>
                    Ya da soldaki türlerden birini ağaca sürükleyerek yeni bir adım oluşturun.
                  </div>
                  <button type="button" className="lcs-btn lcs-btn--primary" onClick={() => addNode(null)}>
                    <i className="bi bi-plus-lg"></i>Açılış butonu ekle
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <ScenarioPreview
          tree={tree}
          startNodeId={previewStart}
          onEditNode={(n) => { setTab("flow"); selectNode(n); }}
        />
      )}

      {deleting && (
        <DeleteScenarioNodeModal
          node={deleting}
          busy={deleteBusy}
          error={deleteError}
          onClose={() => setDeleting(null)}
          onDelete={confirmDelete}
          onPark={parkFromDelete}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 2100,
          background: toast.kind === "success" ? "#111827" : "#dc2626", color: "#fff",
          padding: "10px 18px", borderRadius: 12, fontSize: ".84rem", fontWeight: 600,
          boxShadow: "0 10px 30px rgba(0,0,0,.2)", maxWidth: "calc(100vw - 32px)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className={`bi ${toast.kind === "success" ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`}></i>
          {toast.text}
        </div>
      )}
    </div>
  );
}
